import store from '@/store';
import { checkJwtRoles } from '@/router/guards/helpers';

export const DEFAULT_SITTER_DESTINATION = '/account/providers/dashboard';
export const DEFAULT_SEEKER_DESTINATION = '/account/seekers/dashboard';

export function currentUserIsSitter(): boolean {
  const account = store.getters['account/getAccount'];

  if (account && typeof account.isSitter === 'function') {
    return !!account.isSitter();
  }

  return checkJwtRoles().isSitter;
}

export function resolvePostVerifyDestination(
  query: Record<string, string | (string | null)[] | undefined | null>,
  isSitter: boolean,
  fallbackReturnPath?: string | null
): string {
  const rawNext = query.next ?? query.returnTo ?? query.return_path ?? fallbackReturnPath;
  const candidate = Array.isArray(rawNext) ? rawNext[0] : rawNext;

  if (typeof candidate === 'string' && isSafeInternalPath(candidate) && !isVerifyEmailPath(candidate)) {
    return candidate;
  }

  return isSitter ? DEFAULT_SITTER_DESTINATION : DEFAULT_SEEKER_DESTINATION;
}

export function isSafeInternalPath(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) {
    return false;
  }

  let decoded = path;
  try {
    // Decode repeatedly so %252f / %2509 style encodings cannot bypass checks.
    for (let i = 0; i < 3; i += 1) {
      const next = decodeURIComponent(decoded.replace(/\+/g, '%20'));
      if (next === decoded) {
        break;
      }
      decoded = next;
    }
  } catch {
    return false;
  }

  // Control chars can be stripped by browsers when assigned to location.href,
  // turning "/%09/%09evil.example" into protocol-relative "//evil.example".
  if (hasAsciiControlChars(decoded)) {
    return false;
  }

  // Browsers can normalize backslashes into protocol-relative URLs (e.g. /\evil.example).
  if (decoded.includes('\\') || path.includes('\\') || /%5c/i.test(path)) {
    return false;
  }

  if (decoded.startsWith('//') || decoded.includes('://') || path.includes('://') || /%2f%2f/i.test(path)) {
    return false;
  }

  if (!decoded.startsWith('/')) {
    return false;
  }

  return true;
}

function hasAsciiControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) {
      return true;
    }
  }
  return false;
}

export function isVerifyEmailPath(path: string): boolean {
  const pathname = path.split(/[?#]/)[0];
  return pathname === '/verify-email' || pathname.startsWith('/verify-email/');
}

/**
 * Builds the hard-gate URL used when placement policy says verify now.
 * Safe `returnPath` values become `?next=` so VerifyEmailPage can resume
 * after verification (see resolvePostVerifyDestination). Unsafe paths are dropped.
 */
export function buildVerifyEmailPath(returnPath?: string | null): string {
  if (typeof returnPath === 'string' && isSafeInternalPath(returnPath) && !isVerifyEmailPath(returnPath)) {
    return `/verify-email?next=${encodeURIComponent(returnPath)}`;
  }

  return '/verify-email';
}
